import React, { useCallback, useEffect, useRef } from 'react';
import type {
	ChildResult,
	RenderChildrenProps,
} from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { components } from '@atlassian/jira-command-palette/src/common/ui/components/index.tsx';
import { useDebouncedLoadChildren } from '@atlassian/jira-command-palette/src/controllers/use-debounced-load-children/index.tsx';
import { useQuickSearchAnalytics } from '../../../controllers/use-quick-search-analytics/index.tsx';
import { useQuickSearchWithRecentCache } from '../../../controllers/use-quick-search-with-cache/index.tsx';
import { useGetRecentResults } from './recent/index.tsx';
import { useGetSearchResults } from './search/index.tsx';

export const useQuickSearchLoadChildren = () => {
	const { fetch } = useQuickSearchWithRecentCache();
	const getRecentResults = useGetRecentResults();
	const getSearchResults = useGetSearchResults();
	const [{ searchSessionId }, { setQuery, setIsCachedResult, setScreenName }] =
		useQuickSearchAnalytics();

	const controllerRef = useRef<AbortController | null>(null);

	useEffect(() => () => controllerRef.current?.abort(), []);

	const isMountedRef = useRef(false);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const loadChildren = useCallback(
		async ({ search }: { search: string }): Promise<ChildResult> => {
			controllerRef.current?.abort();
			controllerRef.current = new AbortController();

			let response: Awaited<ReturnType<typeof fetch>> | undefined;
			let error: Error | undefined;
			try {
				setQuery(search);
				response = await fetch(search, searchSessionId, controllerRef.current?.signal);
			} catch (e) {
				error = e instanceof Error ? e : new Error('Error not identified');
			}
			setIsCachedResult(response?.isCache || false);

			if (search && search.length > 0) {
				setScreenName('postQuerySearchResults');

				return {
					...getSearchResults({
						search,
						quickSearchResponseData: response?.result,
						error,
					}),
				};
			}

			setScreenName('preQuerySearchResults');

			return {
				...getRecentResults({
					search,
					quickSearchResponseData: response?.result,
					error,
				}),
			};
		},
		[
			setIsCachedResult,
			setScreenName,
			getRecentResults,
			setQuery,
			fetch,
			searchSessionId,
			getSearchResults,
		],
	);

	return {
		loadChildren,
	};
};

export const QuickSearchChildren = ({
	setChildResult,
	setLoading,
	...rest
}: RenderChildrenProps): React.JSX.Element => {
	const { loadChildren } = useQuickSearchLoadChildren();

	const loadChildCommands = useCallback(
		(searchTerm: string, obj: { isMounted: boolean }) =>
			loadChildren({ search: searchTerm }).then((result) => {
				if (!obj.isMounted) return;
				setChildResult?.(result);
			}),
		[loadChildren, setChildResult],
	);

	useDebouncedLoadChildren(loadChildCommands);

	return <components.Body setChildResult={setChildResult} setLoading={setLoading} {...rest} />;
};
